import { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link as NavigationLink } from "react-router-dom";
import { useDebounce } from "use-debounce";
import { mapKeys, camelCase } from "lodash";
import { useTranslation } from "react-i18next";

import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import CloseIcon from "@mui/icons-material/Close";
import { Popover, TableContainer } from "@mui/material";
import { styled } from "@mui/material/styles";

import { NoDataMessage } from "src/components/NoDataMessage";
import { overviewTableMaxHeightSx } from "src/components/overviewTableStyles";
import CourseStatusIcon from "../CourseStatusIcon";
import { formatShortName, formatDate } from "../../services/formatters";

import Button from "../Button";
import OperationDetailView from "../../features/compliance/shared/OperationDetailView";
import PositionDetailView from "../../features/compliance/shared/PositionDetailView";
import CertificateActions from "../../context/CertificateActions";
import CurrentUserCrewContext from "../../context/CurrentUserCrewContext";
import CourseHistory from "../../features/course-history";

import "./index.scss";

const StickyTableCell = styled(TableCell)(({ theme }) => ({
  "&.MuiTableCell-head": {
    position: "sticky",
    zIndex: theme.zIndex.appBar - 3,
    width: "200px",
    "&:first-of-type": {
      left: 0,
      zIndex: theme.zIndex.appBar - 1,
    },
  },
  "&.MuiTableCell-body": {
    left: 0,
    backgroundColor: "white",
    position: "sticky",
    zIndex: 1, // Low z-index so footer can be on top
  },
}));

// The table scrolls inside its own container so that both header rows stay pinned while scrolling
// (same approach as the aircraft overview table). MUI's `stickyHeader` puts every head cell at
// `top: 0`, so the second header row has to be offset by the height of the first one.
const getTableContainerSx = (secondHeaderRowTop) => ({
  position: "relative",
  overflow: "auto",
  ...overviewTableMaxHeightSx,
  "& thead tr:nth-of-type(2) th": { top: `${secondHeaderRowTop}px` },
});

// The columns and rows on screen; the Excel export uses it too, so the file matches the table
export const getVisibleOverview = ({
  users: users_,
  positions: positions_,
  searchUsers: searchUsersVal,
  searchRoles,
  searchPositions,
  searchOperations: searchOp,
}) => {
  // Start with all data
  let filteredUsers = users_;
  let filteredPositions = positions_;
  let opIndexes = null;

  // Step 1: Filter users by ID if search is provided
  if (searchUsersVal && searchUsersVal.length > 0) {
    const searchIds = Array.isArray(searchUsersVal) ? searchUsersVal : [searchUsersVal];
    filteredUsers = filteredUsers.filter((user) => searchIds.includes(user.id));
  }

  // Step 2: Build initial opIndexes based on role/position/operation filters
  // For each filter, mark which columns (from flattened positions_) should be included
  if (searchRoles || searchPositions || searchOp) {
    opIndexes = {};
    let colIndex = 0;

    // Iterate through all columns and check if they match any filter
    positions_.forEach((posGroup) => {
      posGroup.forEach((col) => {
        let includeColumn = true;

        // Apply role filter
        if (searchRoles && searchRoles.length > 0) {
          includeColumn = includeColumn && searchRoles.includes(col.role_id);
        }

        // Apply position filter
        if (searchPositions && searchPositions.length > 0) {
          includeColumn = includeColumn && searchPositions.includes(col.position_id);
        }

        // Apply operation filter
        if (searchOp && searchOp.length > 0) {
          includeColumn = includeColumn && col.operation_id && searchOp.includes(col.operation_id);
        }

        if (includeColumn) {
          opIndexes[colIndex] = true;
        }

        colIndex++;
      });
    });
  }

  // Step 3: Build position group info to identify position-level columns
  let totalColumns = 0;
  let columnIndex = 0;
  const positionGroupInfo = [];

  positions_.forEach((positionGroup) => {
    const info = {
      positionId: positionGroup[0].position_id,
      roleId: positionGroup[0].role_id,
      hasPositionLevel: positionGroup[0]?.is_position_level,
      positionLevelIndex: positionGroup[0]?.is_position_level ? columnIndex : null,
      operationIndices: [],
    };

    positionGroup.forEach((col) => {
      if (!col.is_position_level) {
        info.operationIndices.push(columnIndex);
      }
      columnIndex++;
    });

    totalColumns = columnIndex;

    if (info.hasPositionLevel) {
      positionGroupInfo.push(info);
    }
  });

  // Step 4: Check which position-level columns should be shown
  // Only show if at least one user has position status but NO operation statuses for that position
  const neededPositionLevelColumns = new Set();
  filteredUsers.forEach((user) => {
    positionGroupInfo.forEach(({ positionLevelIndex, operationIndices, positionId, roleId }) => {
      const positionStatus = user.positions[positionLevelIndex + 1]; // +1 because positions[0] is username

      // Check if user has any operation statuses for this position
      const hasAnyOperationStatus = operationIndices.some((opIndex) => {
        const opStatus = user.positions[opIndex + 1];
        return opStatus && opStatus !== "";
      });

      // Only mark as needed if user has position status but NO operation statuses
      if (positionStatus && positionStatus !== "" && !hasAnyOperationStatus) {
        neededPositionLevelColumns.add(positionLevelIndex);
      }
    });
  });

  // Step 5: Count data presence for each column across filtered users
  const columnDataCount = {};

  // Count how many filtered users have data in each column
  for (let colIdx = 0; colIdx < totalColumns; colIdx++) {
    columnDataCount[colIdx] = 0;

    // Check if this is a position-level column that should be excluded
    const isPositionLevelColumn = positionGroupInfo.some((info) => info.positionLevelIndex === colIdx);

    if (isPositionLevelColumn && !neededPositionLevelColumns.has(colIdx)) {
      // Skip position-level columns that aren't needed
      continue;
    }

    filteredUsers.forEach((user) => {
      const status = user.positions[colIdx + 1]; // +1 because positions[0] is username
      if (status && status !== "" && (!opIndexes || opIndexes[colIdx])) {
        columnDataCount[colIdx]++;
      }
    });
  }

  // Step 6: Build opIndexes based on columns with data count > 0
  const finalOpIndexes = {};
  let hasAnyColumnFilter = false;

  for (let colIdx = 0; colIdx < totalColumns; colIdx++) {
    if (columnDataCount[colIdx] > 0) {
      finalOpIndexes[colIdx] = true;
      hasAnyColumnFilter = true;
    }
  }

  // Step 7: Filter positions based on finalOpIndexes
  if (hasAnyColumnFilter) {
    const posIxs = {};
    let offset = 0;

    for (let posIx = 0; posIx < positions_.length; posIx++) {
      const position = positions_[posIx];
      let show = false;

      for (let opIx = 0; opIx < position.length; opIx++) {
        if (finalOpIndexes[offset + opIx]) {
          show = true;
          break;
        }
      }

      offset += position.length;
      if (show) {
        posIxs[posIx] = true;
      }
    }

    // Filter positions to only show groups with data
    offset = 0;
    filteredPositions = positions_
      .map((it, ix) => {
        let res;
        if (posIxs[ix]) {
          res = it.filter((_op, opIx) => finalOpIndexes[offset + opIx]);
        } else {
          res = null;
        }
        offset += it.length;
        return res;
      })
      .filter((it) => it);

    opIndexes = finalOpIndexes;
  }

  // Step 8: Count data presence for each user across visible columns
  const userDataCount = {};
  filteredUsers.forEach((user) => {
    userDataCount[user.id] = 0;
    for (let colIdx = 0; colIdx < totalColumns; colIdx++) {
      if (!opIndexes || opIndexes[colIdx]) {
        const status = user.positions[colIdx + 1];
        if (status && status !== "") {
          userDataCount[user.id]++;
        }
      }
    }
  });

  // Step 9: Filter users to only show those with data count > 0
  filteredUsers = filteredUsers.filter((user) => userDataCount[user.id] > 0);

  return {
    users: filteredUsers,
    positions: filteredPositions,
    opIndexes,
  };
};

const PositionOverviewTable = ({
  positions: positions_ = [],
  users: users_ = [],
  crewId,
  batchComplianceData = null, // Already fetched batch data
  searchOperations: searchOperationsVal,
  searchRoles: searchRolesVal,
  searchPositions: searchPositionsVal,
  searchUsers: searchUsersVal = "",
  searchOpRef,
  searchRolesRef,
  searchPositionsRef,
  initialLoadComplete = false,
}) => {
  const { t } = useTranslation();
  const [searchOp_, { cancel }] = useDebounce(searchOperationsVal, 400, {
    maxWait: 1500,
  });
  const [searchRoles_, { cancel: cancelRoles }] = useDebounce(searchRolesVal, 400, {
    maxWait: 1500,
  });
  const [searchPositions_, { cancel: cancelPositions }] = useDebounce(searchPositionsVal, 400, {
    maxWait: 1500,
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [details, setDetails] = useState(null);
  const [filteredComplianceData, setFilteredComplianceData] = useState(null);
  const [positionCoursesExpanded, setPositionCoursesExpanded] = useState(false);
  const [popoverKey, setPopoverKey] = useState(0);
  const [isRepositioning, setIsRepositioning] = useState(false);
  const [historyItem, setHistoryItem] = useState(null);

  useEffect(() => {
    if (details?.id && details?.prop && batchComplianceData && batchComplianceData[details.id]) {
      // Use already fetched batch data, filter to show only the clicked operation or position
      const userData = batchComplianceData[details.id];

      // Check if this is a position-level click (no operation_id or is_position_level flag)
      const isPositionLevel = details.prop.isPositionLevel || !details.prop.operationId;

      if (isPositionLevel) {
        // For position-level, show position courses only (no operations)
        const filteredData = {
          certificateMap: userData.certificateMap,
          relationTree: {
            roles:
              userData.relationTree.roles
                .filter((role) => role.id === details.prop.roleId)
                .map((role) => ({
                  ...role,
                  positions:
                    role.positions
                      ?.filter((position) => position.id === details.prop.positionId)
                      .map((position) => ({
                        ...position,
                        operations: [], // No operations for position-level view
                      })) || [],
                })) || [],
          },
        };
        setFilteredComplianceData(filteredData);
      } else {
        // Filter to show only the clicked operation
        const filteredData = {
          certificateMap: userData.certificateMap,
          relationTree: {
            roles:
              userData.relationTree.roles
                .filter((role) => role.id === details.prop.roleId)
                .map((role) => ({
                  ...role,
                  positions:
                    role.positions
                      ?.filter((position) => position.id === details.prop.positionId)
                      .map((position) => ({
                        ...position,
                        operations: position.operations?.filter((op) => op.id === details.prop.operationId) || [],
                      })) || [],
                })) || [],
          },
        };
        setFilteredComplianceData(filteredData);
      }
    }
  }, [details?.id, details?.prop, batchComplianceData]);

  // use debounced value only if the search element is active and the current input value is non-empty
  // otherwise we can just update the display immediately
  let searchOp;
  if (!searchOperationsVal || document.activeElement !== searchOpRef?.current) {
    searchOp = searchOperationsVal;
    // no need for a delayed update as we'll already be using the up to date value
    setTimeout(cancel, 0);
  } else {
    searchOp = searchOp_;
  }

  let searchRoles;
  if (!searchRolesVal || document.activeElement !== searchRolesRef?.current) {
    searchRoles = searchRolesVal;
    // no need for a delayed update as we'll already be using the up to date value
    setTimeout(cancelRoles, 0);
  } else {
    searchRoles = searchRoles_;
  }

  let searchPositions;
  if (!searchPositionsVal || document.activeElement !== searchPositionsRef?.current) {
    searchPositions = searchPositionsVal;
    // no need for a delayed update as we'll already be using the up to date value
    setTimeout(cancelPositions, 0);
  } else {
    searchPositions = searchPositions_;
  }

  const { users, positions, opIndexes } = useMemo(
    () =>
      getVisibleOverview({
        users: users_,
        positions: positions_,
        searchUsers: searchUsersVal,
        searchRoles,
        searchPositions,
        searchOperations: searchOp,
      }),
    [searchOp, searchRoles, searchPositions, searchUsersVal, positions_, users_],
  );

  const firstHeaderRowRef = useRef(null);
  const [firstHeaderRowHeight, setFirstHeaderRowHeight] = useState(0);

  const updateFirstHeaderRowHeight = useCallback(
    () => setFirstHeaderRowHeight(firstHeaderRowRef.current?.offsetHeight ?? 0),
    [],
  );

  useLayoutEffect(() => {
    updateFirstHeaderRowHeight();

    const element = firstHeaderRowRef.current;
    if (!element || typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateFirstHeaderRowHeight);
      return () => window.removeEventListener("resize", updateFirstHeaderRowHeight);
    }

    const observer = new ResizeObserver(updateFirstHeaderRowHeight);
    observer.observe(element);
    return () => observer.disconnect();
  }, [positions, users, updateFirstHeaderRowHeight]);

  const tableContainerSx = useMemo(() => getTableContainerSx(firstHeaderRowHeight), [firstHeaderRowHeight]);

  const statusClicked = (e, d) => {
    setAnchorEl(e.currentTarget);
    const prop = mapKeys(positions.flat()[d.op], (_v, k) => camelCase(k));
    setDetails({ ...d, prop });
  };

  const handleClose = () => {
    setAnchorEl(null);
    setDetails(null);
    setFilteredComplianceData(null);
    setPositionCoursesExpanded(false);
    setPopoverKey(0);
    setIsRepositioning(false);
  };

  const handlePositionCoursesToggle = (expanded) => {
    setPositionCoursesExpanded(expanded);
    setIsRepositioning(true);
    setTimeout(() => {
      setPopoverKey((prev) => prev + 1);
      setIsRepositioning(false);
    }, 150);
  };

  const handleShowHistory = useCallback((certificate) => {
    setHistoryItem(certificate);
  }, []);

  // Check if there are any positions or users to display after filtering
  const hasPositions = positions.length > 0;
  const hasUsers = users.length > 0;

  // Show "No Data" if initial load has finished and there's nothing to display
  const showNoData = initialLoadComplete && (!hasPositions || !hasUsers);

  if (showNoData) {
    return <NoDataMessage />;
  }

  return (
    <>
      <TableContainer sx={tableContainerSx}>
        <Table stickyHeader size="small" className={"crew-table"}>
          <TableHead>
            <TableRow ref={firstHeaderRowRef}>
              <StickyTableCell>&nbsp;</StickyTableCell>
              {positions.map((position) => (
                <StickyTableCell key={position[0].position_name} className={"subcat"} colSpan={position.length}>
                  <span>{position[0].position_name}</span>
                </StickyTableCell>
              ))}
            </TableRow>
            <TableRow>
              <StickyTableCell className={"cell"} />
              {positions.map((position) =>
                position.map((op) => (
                  <StickyTableCell
                    key={`${op.role_id}-${op.position_id}-${op.operation_id || "position"}`}
                    align="left"
                    className={"rotate-cell cell"}
                  >
                    <p className={"rotate"}>
                      <span className={"inner"} title={op.is_position_level ? "Position" : op.operation_name}>
                        {op.is_position_level ? "" : op.operation_name}
                      </span>
                    </p>
                  </StickyTableCell>
                )),
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <StickyTableCell style={{ whiteSpace: "nowrap" }} className={"cell"} title={user.positions[0]}>
                  {formatShortName(user.positions[0])}
                </StickyTableCell>
                {user.positions
                  .slice(1)
                  .filter((_op, ix) => !opIndexes || opIndexes[ix])
                  .map((data, ix) => (
                    <TableCell
                      key={ix}
                      align="center"
                      padding="none"
                      style={{ verticalAlign: "bottom" }}
                      className={"cell"}
                    >
                      {data && (
                        <Button
                          icon={<CourseStatusIcon status={data} />}
                          onClick={(e) =>
                            statusClicked(e, {
                              id: user.id,
                              name: user.positions[0],
                              op: ix,
                            })
                          }
                        />
                      )}
                    </TableCell>
                  ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Popover
        key={popoverKey}
        className={"co-details"}
        anchorEl={anchorEl}
        open={!!anchorEl && !isRepositioning}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        slotProps={{
          paper: {
            sx: {
              maxHeight: "calc(100vh - 32px)",
              maxWidth: "90vw",
              padding: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            },
          },
        }}
        disablePortal={false}
        marginThreshold={16}
      >
        <Box sx={{ position: "relative", display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
          <div className={"toolbar right"}>
            <Button icon={<CloseIcon />} size={"small"} onClick={handleClose} />
          </div>
          <Box
            sx={{ overflowY: "auto", overflowX: "hidden", flex: 1, minHeight: 0, padding: "15px", paddingTop: "10px" }}
          >
            {filteredComplianceData ? (
              <CertificateActions.Provider
                value={{
                  showHistory: handleShowHistory,
                  getBoughtCourses() {
                    return [];
                  },
                }}
              >
                <CurrentUserCrewContext.Provider value={{ userId: details.id, crewId: crewId }}>
                  {(() => {
                    const role = filteredComplianceData.relationTree.roles[0];
                    const position = role?.positions[0];
                    const operation = position?.operations?.[0];
                    const isPositionLevel = details.prop.isPositionLevel || !details.prop.operationId;

                    // Determine what to display
                    const displayName = details.prop?.operationName || details.prop?.positionName || "Operations";
                    const status = isPositionLevel ? position?.status : operation?.status;
                    const validUntil = isPositionLevel ? position?.validUntil : operation?.validUntil;
                    const navigateTo = isPositionLevel ? "positions" : "operations";
                    const scrollToId = isPositionLevel ? `position-${position?.id}` : `operation-${operation?.id}`;

                    return (
                      <>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mb: 2,
                            pr: 5,
                          }}
                        >
                          <NavigationLink
                            to={`/crew/${crewId}/members/${details.id}/compliance/${navigateTo}#${scrollToId}`}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <CourseStatusIcon status={status} />
                              <Typography variant="body1">{`${details.name} > ${displayName}`}</Typography>
                            </Box>
                          </NavigationLink>
                          {validUntil && (
                            <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: "nowrap" }}>
                              {t("valid_until")}: {formatDate(validUntil)}
                            </Typography>
                          )}
                        </Box>
                        {isPositionLevel ? (
                          // Position level: show position courses directly
                          <PositionDetailView
                            position={position}
                            certificateMap={filteredComplianceData.certificateMap}
                          />
                        ) : (
                          // Operation level: show position courses collapsed first, then operation courses
                          <OperationDetailView
                            operation={operation}
                            position={position}
                            certificateMap={filteredComplianceData.certificateMap}
                            showPositionCoursesCollapsed={true}
                            defaultPositionCoursesExpanded={positionCoursesExpanded}
                            onExpandChange={handlePositionCoursesToggle}
                          />
                        )}
                      </>
                    );
                  })()}
                </CurrentUserCrewContext.Provider>
              </CertificateActions.Provider>
            ) : null}
          </Box>
        </Box>
      </Popover>
      {historyItem && filteredComplianceData && details?.id && (
        <CourseHistory
          userId={details.id}
          certificate={historyItem}
          certificateMap={filteredComplianceData.certificateMap}
          onClose={() => setHistoryItem(null)}
          className="co-details-history"
        />
      )}
    </>
  );
};

export default PositionOverviewTable;
